import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gpuAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Map instance status → Sarv design-system .status variant class.
const STATUS_VARIANT = {
  running:    '',
  pending:    'paused',
  stopped:    'draft',
  failed:     'error',
  terminated: 'error',
  unknown:    'draft',
};
function StatusBadge({ status }) {
  const key = (status || 'unknown').toLowerCase();
  const variant = key in STATUS_VARIANT ? STATUS_VARIANT[key] : STATUS_VARIANT.unknown;
  // 'unknown' = GPU-server agent offline → can't verify. Show 'Unreachable'.
  const isUnreachable = key === 'unknown';
  return (
    <span className={`status${variant ? ' ' + variant : ''}`}
      title={isUnreachable ? "Agent offline — live status can't be verified" : undefined}>
      <span className="pulse" />
      {isUnreachable ? 'Unreachable' : (status || 'unknown')}
    </span>
  );
}
function copyToClipboard(text) {
  if (!text) return;
  if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => _fallback(text));
  else _fallback(text);
}
function _fallback(text) {
  const ta = document.createElement('textarea'); ta.value = text;
  document.body.appendChild(ta); ta.select();
  try { document.execCommand('copy'); } catch (e) {}
  document.body.removeChild(ta);
}

// A labelled value row with Copy (and optional show/hide for secrets).
function Field({ label, value, secret, mono = true }) {
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);
  if (value === undefined || value === null || value === '') return null;
  const display = secret && !shown ? '••••••••••••' : value;
  const doCopy = () => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1200); };
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 0', borderBottom:'1px solid var(--surface-2)' }}>
      <span style={{ width:110, color:'var(--muted)', fontSize:12, fontWeight:600 }}>{label}</span>
      <code style={{ flex:1, fontSize:12, color:'var(--ink)', fontFamily: mono ? 'var(--font-mono)':'inherit',
        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{display}</code>
      {secret && (
        <button onClick={() => setShown(s => !s)} className="btn btn-secondary btn-sm">{shown ? 'Hide' : 'Show'}</button>
      )}
      <button onClick={doCopy} className="btn btn-secondary btn-sm">{copied ? 'Copied' : 'Copy'}</button>
    </div>
  );
}

export default function GpuInstanceDetailPage() {
  const { id } = useParams();          // allocation id
  const navigate = useNavigate();
  const { hasSectionAction } = useAuth();
  const canEdit   = hasSectionAction('gpu', 'edit');
  const canDelete = hasSectionAction('gpu', 'delete');
  const [alloc, setAlloc]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [tab, setTab]       = useState('overview');   // overview | logs | terminal

  // logs
  const [logs, setLogs] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [tail, setTail] = useState(200);
  const logsRef = useRef();

  // terminal
  const [termInput, setTermInput] = useState('');
  const [termHistory, setTermHistory] = useState([]);
  const [termRunning, setTermRunning] = useState(false);
  const termBottomRef = useRef();

  // update-image (recreate pod, keep PVC — mirrors CPU)
  const [newImage, setNewImage] = useState('');
  const [updatingImg, setUpdatingImg] = useState(false);
  const [imgMsg, setImgMsg] = useState(null); // { type:'ok'|'err'|'progress', text }

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await gpuAPI.myAllocations();
      const a = (r.data || []).find(x => String(x.id) === String(id));
      if (!a) setError('Instance not found (it may have been terminated).');
      setAlloc(a || null);
    } catch (e) { setError(e.response?.data?.detail || 'Failed to load instance'); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const fetchLogs = useCallback(async (t = tail) => {
    setLogsLoading(true);
    try { const r = await gpuAPI.allocationLogs(id, t); setLogs(r.data.logs || r.data.error || '(no logs)'); }
    catch (e) { setLogs(e.response?.data?.detail || 'Failed to fetch logs'); }
    finally { setLogsLoading(false); }
  }, [id, tail]);
  useEffect(() => { if (tab === 'logs') fetchLogs(tail); }, [tab]); // eslint-disable-line
  useEffect(() => { if (logsRef.current) logsRef.current.scrollTop = logsRef.current.scrollHeight; }, [logs]);

  const runTerm = async (e) => {
    e.preventDefault();
    const cmd = termInput.trim(); if (!cmd) return;
    setTermHistory(h => [...h, { type:'in', text:cmd }]); setTermInput(''); setTermRunning(true);
    try {
      const r = await gpuAPI.execInAllocation(id, cmd);
      const out = (r.data.stdout || '') + (r.data.stderr || '');
      setTermHistory(h => [...h, { type:'out', text: out || '(no output)' }]);
      if (r.data.bounded_seconds) {
        setTermHistory(h => [...h, { type:'out', text: `… (output stopped after ${r.data.bounded_seconds}s — this terminal isn't interactive; add a limit like \`ping -c 4 host\`)` }]);
      }
    } catch (err) {
      setTermHistory(h => [...h, { type:'err', text: err.response?.data?.detail || 'exec failed' }]);
    } finally { setTermRunning(false); setTimeout(() => termBottomRef.current?.scrollIntoView(), 50); }
  };

  const doAction = async (fn, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try { await fn(); load(); } catch (e) { alert(e.response?.data?.detail || 'Action failed'); }
  };

  // Update the container image: recreates the pod with the new image while KEEPING
  // the PVC (datasets/checkpoints survive). The backend re-provisions synchronously
  // and returns 200 on success or 4xx/502 with an honest reason on failure (image
  // rolled back). Ephemeral state (apt packages, /root) is lost — brief GPU downtime.
  const handleUpdateImage = async () => {
    const img = (newImage || '').trim();
    if (!img || !alloc) { setImgMsg({ type:'err', text:'Pick or enter a new image' }); return; }
    if (img === alloc.docker_image) { setImgMsg({ type:'err', text:'That is already the current image' }); return; }
    if (!alloc.request_id) { setImgMsg({ type:'err', text:'This instance has no request id — cannot update' }); return; }
    if (!window.confirm(
      `Update "${alloc.instance_name}" to image "${img}"?\n\n` +
      `The GPU pod is recreated (brief downtime). Your /app PVC data is KEPT; ` +
      `ephemeral state (installed packages, /root) is lost.`
    )) return;
    setUpdatingImg(true);
    setImgMsg({ type:'progress', text:`Recreating with ${img}… (keeps PVC)` });
    try {
      await gpuAPI.updateImage(alloc.request_id, img);
      setImgMsg({ type:'ok', text:`Updated to ${img} ✓` });
      setNewImage('');
      load();
      setTimeout(() => setImgMsg(null), 6000);
    } catch (e) {
      setImgMsg({ type:'err', text: e.response?.data?.detail || 'Image update failed' });
    } finally { setUpdatingImg(false); }
  };

  if (loading) return <div style={{ padding:32, color:'var(--muted-2)' }}>Loading…</div>;
  if (error && !alloc) return (
    <div style={{ padding:32 }}>
      <div style={{ color:'var(--color-danger-500)', marginBottom:12 }}>{error}</div>
      <button onClick={() => navigate('/gpu')} className="btn btn-secondary btn-sm">← Back to GPU</button>
    </div>
  );
  const a = alloc;
  const sshCmd = a.connect_cmd || (a.ssh_port ? `ssh -p ${a.ssh_port} root@${a.ssh_host}` : '');

  return (
    <div style={{ padding:'24px 32px', maxWidth:1000 }}>
      <div style={{ fontSize:12, color:'var(--muted)', marginBottom:8 }}>
        <span style={{ cursor:'pointer', color:'var(--brand)' }} onClick={() => navigate('/gpu')}>GPU</span> › Instances › {a.instance_name}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:20 }}>
        <div style={{ width:48, height:48, borderRadius:12, background:'var(--brand-soft)',
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:24 }}>⚡</div>
        <div style={{ flex:1 }}>
          <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:'var(--ink)' }}>{a.instance_name}</h1>
          <div style={{ fontSize:12, color:'var(--muted)' }}>{a.gpu_model} · {a.vram_gb}GB VRAM · {a.docker_image}</div>
        </div>
        <StatusBadge status={a.status} />
        {a.status === 'running' && canEdit && <button onClick={() => doAction(() => gpuAPI.stopAllocation(id))} className="btn btn-secondary btn-sm">⏸ Stop</button>}
        {a.status === 'stopped' && canEdit && <button onClick={() => doAction(() => gpuAPI.startAllocation(id))} className="btn btn-secondary btn-sm">▶ Start</button>}
        {/* Restart = recreate the pod, keeping the same name, /app PVC data, SSH password+port.
            Shown for a failed/evicted instance (recover it) and a running one (recreate in place). */}
        {canEdit && a.request_id && (a.status === 'failed' || a.status === 'running') && (
          <button
            onClick={() => doAction(
              () => gpuAPI.resizeInstance(a.request_id, { restart: true }),
              a.status === 'failed'
                ? `Restart ${a.instance_name}? Recreates the pod (data on /app is kept).`
                : `Restart ${a.instance_name}? The pod will be recreated (brief downtime; /app data kept).`
            )}
            className={a.status === 'failed' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
            ↻ Restart
          </button>
        )}
        {canDelete && (
        <button onClick={() => doAction(() => gpuAPI.terminateAlloc(id), `Terminate ${a.instance_name}? This deletes the pod.`)}
          className="btn btn-tinted-danger btn-sm">✕ Terminate</button>
        )}
      </div>

      {/* Update image — recreate the GPU pod from a new image, KEEPING the PVC */}
      {canEdit && a.request_id && a.status === 'running' && (
      <div style={{ marginBottom:20, padding:16, borderRadius:10,
                    border:'1px solid var(--surface-2)', background:'var(--surface-1)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
          <span style={{ fontSize:13, fontWeight:700, color:'var(--ink)' }}>Update image</span>
          <span style={{ fontSize:11, color:'var(--muted-2)' }}>
            pod is recreated — brief downtime, /app PVC data kept
          </span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:12, fontSize:12, color:'var(--muted)' }}>
          <span>current:</span>
          <code style={{ fontFamily:'var(--font-mono)', fontSize:12, padding:'2px 6px', borderRadius:4,
                         background:'var(--surface-2)', color:'var(--ink)' }}>{a.docker_image || '—'}</code>
        </div>
        <div style={{ display:'flex', alignItems:'flex-start', gap:10, flexWrap:'wrap' }}>
          <input className="input" value={newImage} onChange={e => setNewImage(e.target.value)}
            placeholder="new image, e.g. pytorch/pytorch:2.2.0-cuda12.1-cudnn8-runtime or dev-registry.sarv.com/dev-sarvkube/org-7/myimg:v2"
            style={{ minWidth:320, flex:'1 1 320px', maxWidth:560 }} />
          <button onClick={handleUpdateImage} disabled={updatingImg || !newImage} className="btn btn-primary">
            {updatingImg ? '⏳ Updating…' : '↑ Update & recreate'}
          </button>
        </div>
        {imgMsg && imgMsg.text && (
          <div style={{ marginTop:10, fontSize:12,
                        color: imgMsg.type==='err' ? 'var(--color-danger-500)'
                             : imgMsg.type==='ok'  ? 'var(--color-success-700)'
                             : 'var(--muted)' }}>
            {imgMsg.text}
          </div>
        )}
      </div>
      )}

      <div className="tabs-underline" style={{ marginBottom:20 }}>
        {(canEdit ? ['overview','logs','terminal'] : ['overview','logs']).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={tab === t ? 'active' : undefined}>
            {t==='overview'?'⊞ Overview':t==='logs'?'📄 Logs':'▣ Terminal'}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="card" style={{ padding:'8px 20px' }}>
          <Field label="SSH" value={sshCmd} />
          <Field label="Password" value={a.ssh_password} secret />
          <Field label="Web URL" value={a.domain_url} mono={false} />
          <Field label="Jupyter" value={a.jupyter_url} mono={false} />
          <Field label="LLM path" value={a.llm_model_path} />
          {a.databases && Object.entries(a.databases).map(([db,url]) => <Field key={db} label={db} value={url} />)}
          {a.extra_ports && Object.entries(a.extra_ports).map(([p,addr]) => <Field key={p} label={`Port :${p}`} value={addr} />)}
          <Field label="Expires" value={a.expires_at ? new Date(a.expires_at).toLocaleString() : ''} mono={false} />
        </div>
      )}

      {tab === 'logs' && (
        <div className="card" style={{ padding:16 }}>
          <div style={{ display:'flex', gap:8, marginBottom:10, alignItems:'center' }}>
            <button onClick={() => fetchLogs(tail)} className="btn btn-secondary btn-sm" disabled={logsLoading}>{logsLoading?'Loading…':'↻ Refresh'}</button>
            <select value={tail} onChange={e=>{ setTail(Number(e.target.value)); fetchLogs(Number(e.target.value)); }} className="select">
              {[100,200,500,1000].map(n => <option key={n} value={n}>last {n}</option>)}
            </select>
          </div>
          <pre ref={logsRef} style={{ background:'var(--ink)', color:'var(--surface-2)', padding:14, borderRadius:8,
            fontSize:12, maxHeight:480, overflow:'auto', whiteSpace:'pre-wrap', margin:0, fontFamily:'var(--font-mono)' }}>{logs || '(no logs)'}</pre>
        </div>
      )}

      {tab === 'terminal' && (
        <div style={{ background:'var(--ink)', borderRadius:10, padding:16 }}>
          <div style={{ maxHeight:420, overflow:'auto', fontFamily:'var(--font-mono)', fontSize:12, marginBottom:10 }}>
            {termHistory.map((h,i) => (
              <div key={i} style={{ color: h.type==='in'?'var(--color-success-500)':h.type==='err'?'var(--color-danger-500)':'var(--surface-2)', whiteSpace:'pre-wrap' }}>
                {h.type==='in' ? `$ ${h.text}` : h.text}
              </div>
            ))}
            <div ref={termBottomRef} />
          </div>
          <form onSubmit={runTerm} style={{ display:'flex', gap:8 }}>
            <span style={{ color:'var(--color-success-500)', fontFamily:'var(--font-mono)', alignSelf:'center' }}>$</span>
            <input value={termInput} onChange={e=>setTermInput(e.target.value)} disabled={termRunning}
              placeholder={a.status==='running'?'run a command…':'instance not running'}
              style={{ flex:1, background:'var(--ink-2)', color:'var(--surface-2)', border:'1px solid var(--border)',
                borderRadius:6, padding:'7px 10px', fontFamily:'var(--font-mono)', fontSize:12 }} />
            <button type="submit" disabled={termRunning || !termInput.trim()} className="btn btn-secondary btn-sm">{termRunning?'…':'Run'}</button>
          </form>
        </div>
      )}
    </div>
  );
}
